import { useEffect, useState } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { Loader2, ShieldAlert, ShieldCheck } from 'lucide-react';
import { Button } from '@/components/ui/button';

const ADMIN_FUNCTIONS_URL = import.meta.env.VITE_ADMIN_FUNCTIONS_URL || 'https://nwzibkuwrcwqnmnytiaj.supabase.co';
const ADMIN_ANON_KEY = import.meta.env.VITE_ADMIN_ANON_KEY || '';

export default function AdminAccess() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [status, setStatus] = useState<'validating' | 'redirecting' | 'error'>('validating');
  const [error, setError] = useState<string | null>(null);

  const token = searchParams.get('token');
  const clientId = searchParams.get('client_id');

  useEffect(() => {
    if (!token || !clientId) {
      setStatus('error');
      setError('Missing access token or client ID');
      return;
    }

    const validate = async () => {
      try {
        const response = await fetch(`${ADMIN_FUNCTIONS_URL}/functions/v1/validate-access-token`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'apikey': ADMIN_ANON_KEY,
          },
          body: JSON.stringify({ token, client_id: clientId }),
        });

        const result = await response.json();

        if (!response.ok || !result.success) {
          setStatus('error');
          setError(result.error || 'Invalid or expired access token');
          return;
        }

        // Redirect to the magic link URL which will create the session
        if (result.redirect_url) {
          setStatus('redirecting');
          window.location.href = result.redirect_url;
        } else {
          setStatus('error');
          setError('No redirect URL returned');
        }
      } catch (err) {
        setStatus('error');
        setError('Failed to validate access token');
      }
    };

    validate();
  }, [token, clientId]);

  return (
    <div className="min-h-screen flex items-center justify-center bg-zinc-50 dark:bg-zinc-950">
      <div className="text-center max-w-sm">
        {status === 'validating' && (
          <>
            <div className="w-16 h-16 rounded-2xl bg-blue-100 dark:bg-blue-950/30 flex items-center justify-center mx-auto mb-6">
              <Loader2 className="w-8 h-8 text-blue-600 animate-spin" />
            </div>
            <h1 className="text-xl font-semibold text-zinc-900 dark:text-white mb-2">
              Verifying admin access...
            </h1>
            <p className="text-sm text-zinc-500 dark:text-zinc-400">
              Authenticating your BlazePOS admin session
            </p>
          </>
        )}

        {status === 'redirecting' && (
          <>
            <div className="w-16 h-16 rounded-2xl bg-green-100 dark:bg-green-950/30 flex items-center justify-center mx-auto mb-6">
              <ShieldCheck className="w-8 h-8 text-green-600" />
            </div>
            <h1 className="text-xl font-semibold text-zinc-900 dark:text-white mb-2">
              Access verified
            </h1>
            <p className="text-sm text-zinc-500 dark:text-zinc-400">
              Redirecting to dashboard...
            </p>
          </>
        )}

        {status === 'error' && (
          <>
            <div className="w-16 h-16 rounded-2xl bg-red-100 dark:bg-red-950/30 flex items-center justify-center mx-auto mb-6">
              <ShieldAlert className="w-8 h-8 text-red-600" />
            </div>
            <h1 className="text-xl font-semibold text-zinc-900 dark:text-white mb-2">
              Access denied
            </h1>
            <p className="text-sm text-zinc-500 dark:text-zinc-400 mb-6">
              {error || 'This access link is invalid or has expired.'}
            </p>
            <Button variant="outline" onClick={() => navigate('/login')}>
              Go to Login
            </Button>
          </>
        )}
      </div>
    </div>
  );
}
